import React, { useEffect, useRef, useState } from 'react';
import "../AnimatedTextField/textFieldStyles.css";
import PhoneInput, { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const MAX_INDIAN_DIGITS = 10;
// National digits of a +91 number (null for other countries).
const indianDigitsOf = (val) => (
    val && val.startsWith('+91') ? val.replace(/\D/g, '').slice(2) : null
);

// lockCountryCode: the country code stays fixed in the box (Backspace can't
// remove it) — it can only be changed from the country dropdown.
function AnimatedPhoneInput({ label, value, handleInputChange, isMandatory, inputId, isDisabled, disabled,onKeyDown, lockCountryCode = false }) {
    const [countryCode, setCountryCode] = useState('+91');
    // Country picked in the dropdown — kept when the input is reset below.
    const [selectedCountry, setSelectedCountry] = useState('IN');
    const [isFocused, setIsFocused] = useState(false);
    // Bumped to remount the input, clearing the "+91" the library keeps
    // showing after its digits are erased.
    const [resetKey, setResetKey] = useState(0);
    const wrapperRef = useRef(null);
    const isExpanded = isFocused || !!value;
    // The code is locked only while the field is active (focused or filled),
    // so an empty, unfocused field still shows just the label, as before.
    const isCodeLocked = lockCountryCode && isExpanded;

    // Login (locked code): an Indian number stops at 10 digits — typing and
    // pasting past that is blocked rather than only flagged as invalid.
    const limitIndianDigits = lockCountryCode && countryCode === '+91';

    const handleKeyDown = (e) => {
        if (limitIndianDigits && /^\d$/.test(e.key)) {
            const digits = indianDigitsOf(value) || '';
            const { selectionStart, selectionEnd } = e.target;
            // Full, and nothing selected to overwrite.
            if (digits.length >= MAX_INDIAN_DIGITS && selectionStart === selectionEnd) {
                e.preventDefault();
                return;
            }
        }
        if (onKeyDown) {
            onKeyDown(e);
        } else if (e.key.toLowerCase() === 'e' || e.keyCode === 69) {
            e.preventDefault();
        }
    };

    const handlePaste = (e) => {
        if (!limitIndianDigits) return;
        let pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
        if (!pasted) return;
        e.preventDefault();
        // A pasted full number may still carry the 91 code.
        if (pasted.length > MAX_INDIAN_DIGITS && pasted.startsWith('91')) pasted = pasted.slice(2);
        const { selectionStart, selectionEnd } = e.target;
        const existing = selectionStart === selectionEnd ? (indianDigitsOf(value) || '') : '';
        handleInputChange(`+91${(existing + pasted).slice(0, MAX_INDIAN_DIGITS)}`);
    };

    const handleCountrySelectClick = (country) => {
        if (country) {
            setSelectedCountry(country);
            const callingCode = getCountryCallingCode(country);
            setCountryCode(`+${callingCode}`);
        }
    };

    useEffect(() => {
        if (value) {
            const phoneNumber = parsePhoneNumber(value);
            if (phoneNumber) {
                if (phoneNumber.country) setSelectedCountry(phoneNumber.country);
                setCountryCode(`+${getCountryCallingCode(phoneNumber.country)}`);
            }
        }
    }, [value]);

    // Focus is tracked for the whole field (input + country dropdown), so
    // moving between them doesn't count as leaving.
    const handleWrapperFocus = () => setIsFocused(true);
    const handleWrapperBlur = (e) => {
        if (wrapperRef.current?.contains(e.relatedTarget)) return;
        setIsFocused(false);
        // Left empty: clear the leftover locked code so only the label shows.
        if (lockCountryCode && !value) setResetKey((k) => k + 1);
    };

    const phoneInputValue = value
        ? (value.startsWith('+') ? value : `${countryCode}${value}`)
        : (isFocused ? countryCode : undefined);


    return (
        <div
            ref={wrapperRef}
            onFocus={handleWrapperFocus}
            onBlur={handleWrapperBlur}
            className={`custom-input-container custom-phone-input-container m-t-20 ${isExpanded ? 'expanded' : ''}`}
        >
            <label
                htmlFor={`customInput_${inputId}`}
                id={`customInputLabel_${inputId}`}
                className={`custom-input-label ${isExpanded ? 'shrink' : ''}`}
            >
                {label} {isMandatory ? <span style={{ color: 'red' }}>*</span> : null}
            </label>

            <PhoneInput
                key={resetKey}
                id={`customInput_${inputId}`}
                className="custom-phone-input-enquiry"
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onChange={(val) => {
                    handleInputChange(val);
                }}
                maxLength={16}
                limitMaxLength={true}
                defaultCountry={selectedCountry}
                addInternationalOption={true}
                disabled={disabled}
                value={phoneInputValue}
                onCountryChange={handleCountrySelectClick}
                {...(isCodeLocked ? { international: true, countryCallingCodeEditable: false } : {})}
            />
        </div>
    );
}

export default AnimatedPhoneInput;
